import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Save, Upload, FileText, Package, X, Sparkles,
} from "lucide-react";
import { adminAxios, absoluteUrl, formatApiError } from "../../lib/adminApi";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const blankForm = {
  name: "", category: "CCTV Cameras", subcategory: "",
  description: "", image_url: "", images: [], pdf_url: "",
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [imageCandidates, setImageCandidates] = useState([]);
  const imgRef = useRef();
  const pdfRef = useRef();
  const aiRef = useRef();

  const load = async () => {
    try {
      const [{ data: it }, { data: cats }] = await Promise.all([
        adminAxios.get("/admin/products"),
        adminAxios.get("/products/categories"),
      ]);
      setItems(it.products || []);
      setCategories(cats.categories || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(blankForm);
    setImageCandidates([]);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      category: p.category || "Other",
      subcategory: p.subcategory || "",
      description: p.description || "",
      image_url: p.image_url || "",
      images: Array.isArray(p.images) ? p.images : (p.image_url ? [p.image_url] : []),
      pdf_url: p.pdf_url || "",
    });
    setImageCandidates([]);
    setShowForm(true);
  };

  const close = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(blankForm);
    setImageCandidates([]);
  };

  const onImg = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingImg(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await adminAxios.post("/admin/upload/gallery", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      setForm((f) => {
        const next = [...(f.images || []), ...uploaded].filter(Boolean);
        // de-dupe preserving order
        const dedup = Array.from(new Set(next));
        return {
          ...f,
          images: dedup,
          image_url: f.image_url || dedup[0] || "",
        };
      });
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingImg(false);
      if (imgRef.current) imgRef.current.value = "";
    }
  };

  const removeImage = (url) => {
    setForm((f) => {
      const images = (f.images || []).filter((u) => u !== url);
      const cover = f.image_url === url ? (images[0] || "") : f.image_url;
      return { ...f, images, image_url: cover };
    });
  };

  const setCover = (url) => {
    setForm((f) => {
      const images = [url, ...(f.images || []).filter((u) => u !== url)];
      return { ...f, images, image_url: url };
    });
  };

  const onPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminAxios.post("/admin/upload/pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, pdf_url: data.url }));
      toast.success("Brochure uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingPdf(false);
      if (pdfRef.current) pdfRef.current.value = "";
    }
  };

  const onAiPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!showForm) {
      setEditingId(null);
      setForm(blankForm);
      setShowForm(true);
    }
    setExtracting(true);
    toast.info("Reading PDF and extracting product details — this may take 10–20 seconds...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminAxios.post("/admin/products/extract-from-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const cands = Array.isArray(data.image_candidates) ? data.image_candidates : [];
      const initialImages = data.image_url
        ? [data.image_url, ...cands.map((c) => c.url).filter((u) => u !== data.image_url)]
        : cands.map((c) => c.url);
      setForm({
        name: data.name || "",
        category: data.category || "Other",
        subcategory: data.subcategory || "",
        description: data.description || "",
        image_url: data.image_url || (initialImages[0] || ""),
        images: Array.from(new Set(initialImages.filter(Boolean))),
        pdf_url: data.pdf_url || "",
      });
      setImageCandidates(cands);
      toast.success("Auto-filled from brochure — review and save");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExtracting(false);
      if (aiRef.current) aiRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Product name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await adminAxios.put(`/admin/products/${editingId}`, form);
      } else {
        await adminAxios.post("/admin/products", form);
      }
      toast.success(editingId ? "Updated" : "Product added");
      close();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await adminAxios.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      setItems((arr) => arr.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-products-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Products</h1>
          <p className="text-slate-500 text-sm mt-1">
            Add the products you install. Use "Auto-fill from PDF" to extract everything from a brochure automatically.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={aiRef} type="file" accept="application/pdf" onChange={onAiPdf} className="hidden" data-testid="product-ai-input" />
          <button
            onClick={() => aiRef.current?.click()}
            disabled={extracting}
            data-testid="product-ai-extract-btn"
            className="h-10 px-5 rounded-full bg-gradient-to-r from-[#1C6B8A] to-[#0f4a63] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
            title="Upload a PDF — we'll auto-fill name, category, description and image"
          >
            {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {extracting ? "Reading PDF..." : "Auto-fill from PDF"}
          </button>
          <button
            onClick={openNew}
            data-testid="product-add-btn"
            className="h-10 px-5 rounded-full brand-outline-btn text-sm font-semibold inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add manually
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 md:p-6 card-soft" data-testid="product-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">
              {editingId ? "Edit product" : "New product"}
            </h2>
            <button onClick={close} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Product name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. CP Plus 2MP Dome Camera" data-testid="product-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <input
                list="product-cats"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="product-category"
                className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm"
                placeholder="Select or type a category"
              />
              <datalist id="product-cats">
                {categories.map((c) => (<option key={c} value={c} />))}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label>Sub-category</Label>
              <Input
                value={form.subcategory}
                onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                data-testid="product-subcategory"
                placeholder="e.g. Dome / Bullet / PTZ / Fingerprint"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Images (you can pick multiple, auto-resized to 1600px)</Label>
              <input ref={imgRef} type="file" accept="image/*" multiple onChange={onImg} className="hidden" data-testid="product-image-input" />
              <button
                type="button"
                onClick={() => imgRef.current?.click()}
                disabled={uploadingImg}
                data-testid="product-image-btn"
                className="h-10 w-full rounded-full border border-slate-200 hover:border-[#1C6B8A] text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {form.images?.length ? "Add more images" : "Upload images"}
              </button>
            </div>
            <div className="space-y-1.5">
              <Label>Brochure PDF (max 25MB)</Label>
              <input ref={pdfRef} type="file" accept="application/pdf" onChange={onPdf} className="hidden" data-testid="product-pdf-input" />
              <button
                type="button"
                onClick={() => pdfRef.current?.click()}
                disabled={uploadingPdf}
                data-testid="product-pdf-btn"
                className="h-10 w-full rounded-full border border-slate-200 hover:border-[#1C6B8A] text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {uploadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {form.pdf_url ? "Replace PDF" : "Upload PDF"}
              </button>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Description</Label>
              <Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short product description (optional)" data-testid="product-description" />
            </div>
          </div>

          {form.images?.length > 0 && (
            <div className="mt-5" data-testid="product-images-editor">
              <Label className="block mb-2">Product images ({form.images.length})</Label>
              <p className="text-xs text-slate-500 mb-3">
                Click a thumbnail to set it as the cover. The cover is shown first on the website.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2">
                {form.images.map((url) => {
                  const isCover = form.image_url === url;
                  return (
                    <div
                      key={url}
                      data-testid={`product-image-${url.split("/").pop()}`}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 bg-white ${
                        isCover
                          ? "border-[#1C6B8A] ring-2 ring-[#1C6B8A]/30"
                          : "border-slate-200"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setCover(url)}
                        className="block w-full h-full"
                        title={isCover ? "Cover image" : "Set as cover"}
                      >
                        <img
                          src={absoluteUrl(url)}
                          alt=""
                          className="w-full h-full object-contain p-1.5"
                        />
                      </button>
                      {isCover && (
                        <span className="absolute top-1 left-1 bg-[#1C6B8A] text-white text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider">
                          Cover
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(url)}
                        data-testid={`product-image-remove-${url.split("/").pop()}`}
                        className="absolute top-1 right-1 bg-white/95 hover:bg-red-500 hover:text-white text-slate-600 rounded-full w-6 h-6 inline-flex items-center justify-center shadow"
                        title="Remove image"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-5 flex items-center gap-2">
            <button
              onClick={submit}
              disabled={saving}
              data-testid="product-submit"
              className="h-10 px-5 rounded-full brand-btn text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editingId ? "Save changes" : "Add product"}
            </button>
            <button onClick={close} className="h-10 px-5 rounded-full border border-slate-200 text-sm font-semibold">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#1C6B8A]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-500">
          <Package className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          No products yet. Click "Auto-fill from PDF" or "Add manually" to create your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-admin-list">
          {items.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border border-slate-100 overflow-hidden card-soft" data-testid={`admin-product-${p.id}`}>
              <div className="relative aspect-[4/3] bg-white border-b border-slate-100 overflow-hidden">
                {p.image_url ? (
                  <img src={absoluteUrl(p.image_url)} alt={p.name} className="w-full h-full object-contain p-3" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300">
                    <Package className="w-10 h-10" />
                  </div>
                )}
                {p.images?.length > 1 && (
                  <span className="absolute top-2 right-2 bg-slate-900/85 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                    +{p.images.length - 1}
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[#1C6B8A]">
                      {p.category}{p.subcategory ? ` · ${p.subcategory}` : ""}
                    </div>
                    <div className="font-display font-semibold text-slate-900 truncate">{p.name}</div>
                  </div>
                  {p.pdf_url && <FileText className="w-4 h-4 text-emerald-500 shrink-0 mt-1" title="Has PDF" />}
                </div>
                {p.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2 whitespace-pre-line">{p.description}</p>}
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`} className="flex-1 h-9 rounded-full brand-outline-btn text-xs font-semibold">
                    Edit
                  </button>
                  <button onClick={() => remove(p.id)} data-testid={`product-delete-${p.id}`} className="h-9 w-9 rounded-full border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
